import { CoverageTypeEnum } from "@platform/types";

import { PlanRoleEnum } from "../types/proposal.types";

import { matchesProposalCoverageType } from "./coverageTypeNormalizer";
import {
  buildProposalGroupFilter,
  isUnmappedProposalGroup,
  quotedPlanLabel,
} from "./proposalToGrid";

import type {
  BenefitPlan,
  BenefitPlanGroup,
  CarrierBenefitPlanGroup,
  RateSchedule,
} from "@platform/btx-rates";
import type {
  Proposal,
  ProposalCoverage,
  ProposalPlan,
  ProposalPlanGroup,
} from "../types/proposal.types";

const PRESENTATION_POLICY_PERIOD = "current";
const DEFAULT_OPTION_NAME = "notApplicable";

const persistedGroupId = (group: ProposalPlanGroup): string | undefined =>
  !group.isPlaceholder && group.planRole === PlanRoleEnum.QUOTED
    ? group.id
    : undefined;

/** Reorders carrier groups according to a specified carrier ID order. */
const reorderRatesCarrierGroups = (
  groups: CarrierBenefitPlanGroup[],
  carrierIdOrder: string[],
): CarrierBenefitPlanGroup[] => {
  const groupMap = new Map(groups.map((g) => [g.carrierId, g]));
  const ordered: CarrierBenefitPlanGroup[] = [];
  const seen = new Set<string>();

  for (const carrierId of carrierIdOrder) {
    const group = groupMap.get(carrierId);
    if (group) {
      ordered.push(group);
      seen.add(carrierId);
    }
  }

  for (const group of groups) {
    if (!seen.has(group.carrierId)) {
      ordered.push(group);
    }
  }

  return ordered;
};

export const proposalPlanToRatesBenefitPlan = (
  plan: ProposalPlan,
): BenefitPlan => ({
  id: plan.id,
  identifier: plan.name ?? plan.id,
  type: plan.type ?? "",
  // RatesGrid hides only literal "alternate" plans; quote-side proposal plans
  // can carry rate schedules for current and quoted scenarios alike.
  policyPeriod: PRESENTATION_POLICY_PERIOD,
  optionName: plan.optionName ?? DEFAULT_OPTION_NAME,
  rateScheduleSource: "proposal",
  // ProposalRateSchedule and RateSchedule share the same runtime shape;
  // the only real mapping is proposalPlanId → benefitPlanId. The enum types
  // come from different generated packages so we cast through unknown.
  rateSchedule: plan.rateSchedules?.map(
    (s) =>
      ({ ...s, benefitPlanId: s.proposalPlanId }) as unknown as RateSchedule,
  ),
  benefits: plan.benefits as Record<string, unknown> | undefined,
  // Backend-computed premiums (ADR-026), consumed by the rates SummaryGrid.
  monthlyPremium: plan.monthlyPremium,
  annualPremium: plan.annualPremium,
  riderCharges: plan.riderCharges,
});

export const proposalPlanGroupToRatesBenefitPlanGroup = (
  group: ProposalPlanGroup,
): BenefitPlanGroup => ({
  id: group.id,
  productType: group.productType,
  policyPeriod: PRESENTATION_POLICY_PERIOD,
  position: group.position,
  nickname: group.nickname,
  footnote: group.footnote,
  proposalPlanGroupId: persistedGroupId(group),
  isProposalGroup: true,
  size: group.size,
  benefitPlans: group.plans.map(proposalPlanToRatesBenefitPlan),
  totalMonthlyPremium: group.totalMonthlyPremium,
  totalAnnualPremium: group.totalAnnualPremium,
  enrollmentGroupFlagged: group.enrollmentGroupFlagged,
});

export interface ProposalRatesGridData {
  benefitPlanGroups: BenefitPlanGroup[];
  carrierGroups: CarrierBenefitPlanGroup[];
}

export const proposalCoverageToRatesGridData = (
  proposalCoverage: ProposalCoverage,
): ProposalRatesGridData => ({
  benefitPlanGroups: proposalCoverage.planGroups.map(
    proposalPlanGroupToRatesBenefitPlanGroup,
  ),
  carrierGroups: [],
});

export const proposalToRatesGridDataForCoverage = (
  proposal: Proposal,
  coverageType: CoverageTypeEnum,
): ProposalRatesGridData | null => {
  const proposalCoverage = proposal.coverages.find((coverage) =>
    matchesProposalCoverageType(coverage.coverageType, coverageType),
  );

  if (!proposalCoverage) {
    return null;
  }

  return proposalCoverageToRatesGridData(proposalCoverage);
};

export const proposalsToRatesGridData = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
): ProposalRatesGridData | null => {
  const carrierGroups = proposals.flatMap((proposal) => {
    const proposalCoverage = proposal.coverages.find((coverage) =>
      matchesProposalCoverageType(coverage.coverageType, coverageType),
    );

    if (!proposalCoverage) {
      return [];
    }

    return [
      {
        carrierId: proposal.carrierId,
        carrierName: proposal.carrierName,
        benefitPlanGroups: proposalCoverage.planGroups.map(
          proposalPlanGroupToRatesBenefitPlanGroup,
        ),
      },
    ];
  });

  const benefitPlanGroups = carrierGroups.flatMap(
    (carrierGroup) => carrierGroup.benefitPlanGroups,
  );

  if (benefitPlanGroups.length === 0) {
    return null;
  }

  return { benefitPlanGroups, carrierGroups };
};

const flattenCoveragePlansForRates = (
  proposalCoverage: ProposalCoverage,
  groupFilter: (group: ProposalPlanGroup) => boolean,
  startPosition: number,
  isHistorical: boolean,
): BenefitPlanGroup[] => {
  const flatBPGs: BenefitPlanGroup[] = [];
  let position = startPosition;

  for (const group of proposalCoverage.planGroups) {
    if (!groupFilter(group)) continue;

    if (group.plans.length === 0) continue;

    const benefitPlans = group.plans.map((plan) => ({
      ...proposalPlanToRatesBenefitPlan(plan),
      identifier: quotedPlanLabel(plan),
    }));

    flatBPGs.push({
      id: group.id,
      productType: group.productType,
      policyPeriod: PRESENTATION_POLICY_PERIOD,
      position: position++,
      nickname: group.nickname,
      footnote: group.footnote,
      proposalPlanGroupId: persistedGroupId(group),
      isProposalGroup: true,
      size: benefitPlans.length,
      benefitPlans,
      enrollmentGroupFlagged: group.enrollmentGroupFlagged,
      // An unmapped group has no requested plan for a requote to supersede, so its owning
      // proposal's version state describes a sibling plan, not this column. The SBS grid
      // above these rates already refuses that state (see proposalToGrid); disagreeing
      // here would suffix "(Previous)" onto the rates rows for the same quote.
      isHistorical: isHistorical && !isUnmappedProposalGroup(group),
    });
  }

  return flatBPGs;
};

/**
 * PropelX variant of `proposalsToRatesGridData`.
 *
 * Each proposal plan group becomes one BPG, so the rates grid mirrors the
 * carrier grid's Carrier → Group → Plan structure and stays aligned with it.
 *
 * When multiple proposals exist for the same carrier (quote history),
 * they are consolidated under a single carrier group. Active (most recent)
 * plans appear first, followed by historical versions ordered by
 * `receivedAt` descending.
 *
 * @param rfpBenefitPlanGroupId - When provided, only plan groups linked to this RFP BPG are
 *   included — or, when it is the Unmapped Plans page sentinel, only real unmapped carrier groups.
 * @param carrierIdOrder - When provided, carrier groups are sorted according to this order.
 */
export const proposalsToFlatRatesGridData = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
  rfpBenefitPlanGroupId?: string,
  carrierIdOrder?: string[],
): ProposalRatesGridData | null => {
  // Sort proposals so active versions come first per carrier, then by
  // receivedAt descending (newest first) for quote history ordering.
  const sortedProposals = [...proposals].sort((a, b) => {
    if (a.isActiveVersion !== b.isActiveVersion) {
      return a.isActiveVersion ? -1 : 1;
    }
    const aTime = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
    const bTime = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
    return bTime - aTime;
  });

  const groupFilter = buildProposalGroupFilter(rfpBenefitPlanGroupId);

  // Consolidate proposals from the same carrier into one group
  const carrierGroupMap = new Map<string, CarrierBenefitPlanGroup>();
  const carrierGroupOrder: string[] = [];

  for (const proposal of sortedProposals) {
    const proposalCoverage = proposal.coverages.find((coverage) =>
      matchesProposalCoverageType(coverage.coverageType, coverageType),
    );

    if (!proposalCoverage) {
      continue;
    }

    const existingGroup = carrierGroupMap.get(proposal.carrierId);
    const startPosition = existingGroup?.benefitPlanGroups.length ?? 0;
    const flatBPGs = flattenCoveragePlansForRates(
      proposalCoverage,
      groupFilter,
      startPosition,
      !proposal.isActiveVersion,
    );

    if (existingGroup) {
      existingGroup.benefitPlanGroups.push(...flatBPGs);
    } else {
      const newGroup: CarrierBenefitPlanGroup = {
        carrierId: proposal.carrierId,
        carrierName: proposal.carrierName,
        benefitPlanGroups: flatBPGs,
      };
      carrierGroupMap.set(proposal.carrierId, newGroup);
      carrierGroupOrder.push(proposal.carrierId);
    }
  }

  const carrierGroups = carrierGroupOrder
    .map((id) => carrierGroupMap.get(id)!)
    .filter((g) => g.benefitPlanGroups.length > 0);

  // Apply custom carrier ordering when specified
  const orderedCarrierGroups =
    carrierIdOrder && carrierIdOrder.length > 0
      ? reorderRatesCarrierGroups(carrierGroups, carrierIdOrder)
      : carrierGroups;

  const benefitPlanGroups = orderedCarrierGroups.flatMap(
    (carrierGroup) => carrierGroup.benefitPlanGroups,
  );

  if (benefitPlanGroups.length === 0) {
    return null;
  }

  return { benefitPlanGroups, carrierGroups: orderedCarrierGroups };
};
